import React, { useRef } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faImage } from '@fortawesome/free-solid-svg-icons';

export const MAX_DEFECT_IMAGE_BYTES = 10 * 1024 * 1024;

interface DefectImageInputProps {
  onImageSelected: (file: File | null) => void;
  selectedFileName?: string | null;
  onClear: () => void;
  onValidationError: (message: string) => void;
  disabled?: boolean;
}

/**
 * בחירת תמונה לדיווח תקלה: צילום במצלמה (מובייל) או בחירה מהגלריה.
 */
const DefectImageInput: React.FC<DefectImageInputProps> = ({
  onImageSelected,
  selectedFileName,
  onClear,
  onValidationError,
  disabled = false,
}) => {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const f = input.files?.[0] ?? null;
    if (!f) {
      onImageSelected(null);
      return;
    }
    if (!f.type.startsWith('image/')) {
      onValidationError('יש לבחור קובץ תמונה');
      input.value = '';
      return;
    }
    if (f.size > MAX_DEFECT_IMAGE_BYTES) {
      onValidationError('גודל התמונה חייב להיות עד 10 מגה-בייט');
      input.value = '';
      return;
    }
    onImageSelected(f);
    input.value = '';
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-1 align-items-center mb-1">
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={() => cameraRef.current?.click()}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faCamera} className="me-1" />
          צלם במצלמה
        </Button>
        <Button
          type="button"
          variant="outline-secondary"
          size="sm"
          onClick={() => galleryRef.current?.click()}
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faImage} className="me-1" />
          בחר מהגלריה
        </Button>
      </div>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="d-none"
        onChange={handleFile}
        disabled={disabled}
      />
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        className="d-none"
        onChange={handleFile}
        disabled={disabled}
      />
      <Form.Text className="text-muted d-block">JPEG, PNG, GIF או WebP · במובייל אפשר לצלם ישירות</Form.Text>
      {selectedFileName && (
        <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
          <small className="text-success">נבחר: {selectedFileName}</small>
          <Button type="button" variant="link" size="sm" className="p-0" onClick={onClear}>
            הסר תמונה
          </Button>
        </div>
      )}
    </div>
  );
};

export default DefectImageInput;
